import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getAuth } from "firebase/auth";
import {
  collection,
  addDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
  query,
  orderBy,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import {
  ImagePlus,
  Trash2,
  Pencil,
  Power,
  PowerOff,
  X,
  CalendarDays,
  MapPin,
} from "lucide-react";

import InnovationHubHeader from "../components/InnovationHubHeader";
import { db, storage } from "../web/src/firebaseClient";
import InnovationHubFooter from "../components/InnovationHubFooter";

const COLLECTION_NAME = "events";

export default function AdminEventManagement() {
  const navigate = useNavigate();

  const [isAdmin, setIsAdmin] = useState(null);
  const [events, setEvents] = useState([]);
  const [editingEvent, setEditingEvent] = useState(null);

  const [title, setTitle] = useState("");
  const [location, setLocation] = useState("");
  const [eventDate, setEventDate] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [imageFile, setImageFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    checkAdmin();
    fetchEvents();
  }, []);

  const checkAdmin = async () => {
    const user = getAuth().currentUser;

    if (!user) {
      setIsAdmin(false);
      return;
    }

    const token = await user.getIdTokenResult();
    setIsAdmin(token.claims.role === "admin");
  };

  const fetchEvents = async () => {
    const q = query(
      collection(db, COLLECTION_NAME),
      orderBy("eventDate", "desc")
    );

    const snapshot = await getDocs(q);

    const data = snapshot.docs.map((docItem) => ({
      id: docItem.id,
      ...docItem.data(),
    }));

    setEvents(data);
  };

  const toDateInputValue = (dateValue) => {
    if (!dateValue) return "";

    const date = dateValue.toDate ? dateValue.toDate() : new Date(dateValue);
    const localDate = new Date(
      date.getTime() - date.getTimezoneOffset() * 60000
    );

    return localDate.toISOString().slice(0, 16);
  };

  const formatDate = (dateValue) => {
    if (!dateValue) return "Belirtilmedi";

    const date = dateValue.toDate ? dateValue.toDate() : new Date(dateValue);

    return date.toLocaleString("tr-TR", {
      dateStyle: "short",
      timeStyle: "short",
    });
  };

  const handleEditEvent = (eventItem) => {
    setEditingEvent(eventItem);
    setTitle(eventItem.title || "");
    setLocation(eventItem.location || "");
    setEventDate(toDateInputValue(eventItem.eventDate));
    setStartDate(toDateInputValue(eventItem.startDate));
    setEndDate(toDateInputValue(eventItem.endDate));
    setIsActive(Boolean(eventItem.isActive));
    setImageFile(null);
    setPreviewUrl(eventItem.imageUrl || "");

    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];

    if (!file) return;

    setImageFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const resetForm = () => {
    setEditingEvent(null);
    setTitle("");
    setLocation("");
    setEventDate("");
    setStartDate("");
    setEndDate("");
    setIsActive(true);
    setImageFile(null);
    setPreviewUrl("");
  };

  const handleSubmitEvent = async (e) => {
    e.preventDefault();

    if (!title.trim()) {
      alert("Lütfen etkinlik başlığı girin.");
      return;
    }

    if (!location.trim()) {
      alert("Lütfen etkinlik yerini girin.");
      return;
    }

    if (!eventDate) {
      alert("Lütfen etkinlik tarihini seçin.");
      return;
    }

    if (!editingEvent && !imageFile) {
      alert("Lütfen etkinlik görseli seçin.");
      return;
    }

    setLoading(true);

    try {
      let imageUrl = editingEvent?.imageUrl || "";
      let storagePath = editingEvent?.storagePath || "";

      if (imageFile) {
        const fileName = `events/${Date.now()}-${imageFile.name}`;
        const imageRef = ref(storage, fileName);

        await uploadBytes(imageRef, imageFile);
        imageUrl = await getDownloadURL(imageRef);
        storagePath = fileName;

        if (editingEvent?.storagePath) {
          try {
            await deleteObject(ref(storage, editingEvent.storagePath));
          } catch (error) {
            console.warn("Eski görsel silinemedi:", error);
          }
        }
      }

      const payload = {
        title: title.trim(),
        location: location.trim(),
        eventDate: Timestamp.fromDate(new Date(eventDate)),
        startDate: startDate ? Timestamp.fromDate(new Date(startDate)) : null,
        endDate: endDate ? Timestamp.fromDate(new Date(endDate)) : null,
        imageUrl,
        storagePath,
        isActive,
        updatedAt: serverTimestamp(),
      };

      if (editingEvent) {
        await updateDoc(doc(db, COLLECTION_NAME, editingEvent.id), payload);
      } else {
        await addDoc(collection(db, COLLECTION_NAME), {
          ...payload,
          createdAt: serverTimestamp(),
        });
      }

      resetForm();
      await fetchEvents();
    } catch (error) {
      console.error(error);
      alert("Etkinlik kaydedilirken bir hata oluştu.");
    } finally {
      setLoading(false);
    }
  };

  const toggleActive = async (eventItem) => {
    await updateDoc(doc(db, COLLECTION_NAME, eventItem.id), {
      isActive: !eventItem.isActive,
      updatedAt: serverTimestamp(),
    });

    await fetchEvents();
  };

  const deleteEvent = async (eventItem) => {
    const confirmDelete = window.confirm("Bu etkinliği silmek istiyor musunuz?");
    if (!confirmDelete) return;

    try {
      await deleteDoc(doc(db, COLLECTION_NAME, eventItem.id));

      if (eventItem.storagePath) {
        try {
          await deleteObject(ref(storage, eventItem.storagePath));
        } catch (error) {
          console.warn("Görsel silinemedi:", error);
        }
      }

      if (editingEvent?.id === eventItem.id) {
        resetForm();
      }

      await fetchEvents();
    } catch (error) {
      console.error(error);
      alert("Etkinlik silinirken bir hata oluştu.");
    }
  };

  if (isAdmin === null) {
    return (
      <main className="min-h-screen bg-[#eef5ff]">
        <InnovationHubHeader />
        <div className="p-6">Yükleniyor...</div>
      </main>
    );
  }

  if (!isAdmin) {
    return (
      <main className="min-h-screen bg-[#eef5ff]">
        <InnovationHubHeader />
        <div className="p-10 text-center">
          <h2 className="text-xl font-semibold text-red-600">
            Bu sayfaya erişim yetkiniz yok
          </h2>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#eef5ff]">
      <InnovationHubHeader />

      <section className="p-6">
        <div className="mx-auto max-w-6xl space-y-6">
          <div className="rounded-2xl bg-white p-6 shadow-sm">
            <button
              type="button"
              onClick={() => navigate("/admin")}
              className="mb-4 text-sm font-medium text-[#1565C0] hover:underline"
            >
              ← Admin Panel
            </button>

            <h1 className="text-2xl font-semibold text-[#173b7a]">
              Etkinlik Yönetimi
            </h1>

            <p className="mt-2 text-sm text-gray-500">
              Etkinlik görsellerini, tarihlerini, yer bilgilerini ve yayın
              aralıklarını buradan yönetebilirsiniz.
            </p>
          </div>

          <div className="rounded-2xl bg-white p-6 shadow-sm">
            <div className="mb-5 flex items-center justify-between gap-4">
              <h2 className="text-lg font-semibold text-[#173b7a]">
                {editingEvent ? "Etkinlik Güncelle" : "Yeni Etkinlik Ekle"}
              </h2>

              {editingEvent && (
                <button
                  type="button"
                  onClick={resetForm}
                  className="flex items-center gap-2 rounded-xl bg-gray-100 px-4 py-2 text-sm font-semibold text-gray-600 transition hover:bg-gray-200"
                >
                  <X size={16} />
                  Vazgeç
                </button>
              )}
            </div>

            <form onSubmit={handleSubmitEvent} className="space-y-5">
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">
                  Başlık
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Örn: Girişimcilik Atölyesi"
                  className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm outline-none focus:border-[#1565C0]"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">
                  Yer
                </label>
                <input
                  type="text"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder="Örn: Konferans Salonu"
                  className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm outline-none focus:border-[#1565C0]"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">
                  Etkinlik Tarihi
                </label>
                <input
                  type="datetime-local"
                  value={eventDate}
                  onChange={(e) => setEventDate(e.target.value)}
                  className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm outline-none focus:border-[#1565C0]"
                />
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Yayın Başlangıç Tarihi{" "}
                    <span className="font-normal text-gray-400">
                      (opsiyonel)
                    </span>
                  </label>
                  <input
                    type="datetime-local"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm outline-none focus:border-[#1565C0]"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Yayın Bitiş Tarihi{" "}
                    <span className="font-normal text-gray-400">
                      (opsiyonel)
                    </span>
                  </label>
                  <input
                    type="datetime-local"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm outline-none focus:border-[#1565C0]"
                  />
                </div>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">
                  Fotoğraf
                </label>

                <label className="flex cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed border-blue-100 bg-[#f8fbff] p-6 text-center transition hover:border-[#1565C0]/40">
                  <ImagePlus className="mb-2 text-[#1565C0]" size={32} />
                  <span className="text-sm font-medium text-[#173b7a]">
                    {editingEvent ? "Yeni fotoğraf seç" : "Fotoğraf seç"}
                  </span>
                  <span className="mt-1 text-xs text-gray-500">
                    PNG, JPG veya WEBP
                  </span>

                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="hidden"
                  />
                </label>

                {previewUrl && (
                  <div className="mt-4 overflow-hidden rounded-2xl border border-gray-100 bg-gray-50">
                    <img
                      src={previewUrl}
                      alt="Etkinlik önizleme"
                      className="aspect-video w-full object-cover"
                    />
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between rounded-xl bg-[#f8fbff] p-4">
                <div>
                  <p className="text-sm font-medium text-[#173b7a]">
                    Yayın Durumu
                  </p>
                  <p className="text-xs text-gray-500">
                    Aktif seçilirse yayın tarih aralığına göre görünür.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => setIsActive((prev) => !prev)}
                  className={`rounded-full px-4 py-2 text-sm font-semibold transition ${
                    isActive
                      ? "bg-green-100 text-green-700"
                      : "bg-gray-100 text-gray-500"
                  }`}
                >
                  {isActive ? "Aktif" : "Pasif"}
                </button>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full rounded-xl bg-[#1565C0] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#0d4f9c] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {loading
                  ? "Kaydediliyor..."
                  : editingEvent
                  ? "Etkinliği Güncelle"
                  : "Etkinliği Kaydet"}
              </button>
            </form>
          </div>

          <div className="rounded-2xl bg-white p-6 shadow-sm">
            <h2 className="mb-5 text-lg font-semibold text-[#173b7a]">
              Mevcut Etkinlikler
            </h2>

            {events.length === 0 ? (
              <div className="rounded-xl bg-[#f8fbff] p-6 text-center text-sm text-gray-500">
                Henüz etkinlik eklenmemiş.
              </div>
            ) : (
              <div className="space-y-4">
                {events.map((eventItem) => (
                  <div
                    key={eventItem.id}
                    className={`grid grid-cols-1 gap-4 rounded-2xl border p-4 md:grid-cols-[220px_1fr_auto] ${
                      editingEvent?.id === eventItem.id
                        ? "border-[#1565C0] bg-blue-50/60"
                        : "border-gray-100 bg-[#f8fbff]"
                    }`}
                  >
                    <img
                      src={eventItem.imageUrl}
                      alt={eventItem.title || "Etkinlik görseli"}
                      className="aspect-video w-full rounded-xl object-cover"
                    />

                    <div className="space-y-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="font-semibold text-[#173b7a]">
                          {eventItem.title || "Başlıksız Etkinlik"}
                        </h3>

                        <span
                          className={`rounded-full px-3 py-1 text-xs font-semibold ${
                            eventItem.isActive
                              ? "bg-green-100 text-green-700"
                              : "bg-gray-200 text-gray-600"
                          }`}
                        >
                          {eventItem.isActive ? "Aktif" : "Pasif"}
                        </span>
                      </div>

                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <CalendarDays size={15} />
                        <span>Etkinlik: {formatDate(eventItem.eventDate)}</span>
                      </div>

                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <MapPin size={15} />
                        <span>{eventItem.location}</span>
                      </div>

                      <div className="grid grid-cols-1 gap-1 text-xs text-gray-500 md:grid-cols-2">
                        <span>
                          Yayın Başlangıç: {formatDate(eventItem.startDate)}
                        </span>
                        <span>Yayın Bitiş: {formatDate(eventItem.endDate)}</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 md:flex-col">
                      <button
                        type="button"
                        onClick={() => handleEditEvent(eventItem)}
                        className="rounded-lg bg-white p-2 text-[#1565C0] shadow-sm transition hover:bg-blue-50"
                      >
                        <Pencil size={18} />
                      </button>

                      <button
                        type="button"
                        onClick={() => toggleActive(eventItem)}
                        className="rounded-lg bg-white p-2 shadow-sm transition hover:text-[#1565C0]"
                      >
                        {eventItem.isActive ? (
                          <PowerOff size={18} />
                        ) : (
                          <Power size={18} />
                        )}
                      </button>

                      <button
                        type="button"
                        onClick={() => deleteEvent(eventItem)}
                        className="rounded-lg bg-white p-2 text-red-500 shadow-sm transition hover:bg-red-50"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
      <InnovationHubFooter />
    </main>
  );
}